import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import { TopBar } from "@/components/TopBar";
import BayarForm from "@/components/BayarForm";
import {
  apiListCustomers,
  apiListDesigners, apiDesainerBawaan,
  apiListBarang,
  apiGetPengaturan,
  apiPreviewTransaksi,
  apiCreateTransaksi,
  formatApiErrorDetail,
} from "@/lib/api";
import { qty, luasM2, meterKeCm, teksMeter, spkTunggu, rencanaTeks, waktuProduksiTeks } from "@/lib/format";
import DtfMenungguAcc from "@/components/DtfMenungguAcc";
import RincianHitung from "@/components/RincianHitung";
import TanpaPpn from "@/components/TanpaPpn";
import { HargaKhususField, AlasanDiskon } from "@/components/HargaKhususField";
import { fieldHargaKhusus } from "@/lib/hargaKhusus";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Plus, Trash2, Calculator, Save, Printer, X } from "lucide-react";
import { useCetak } from "@/hooks/usePengaturan";
import { useKunciKirim } from "@/hooks/useKunciKirim";
import { desainerAwal } from "@/lib/pecahDesain";  // v2.21
import LinkPublik from "@/components/LinkPublik";
import AccPanel from "@/components/AccPanel";
import WaStatus from "@/components/WaStatus";
import KirimLink from "@/components/KirimLink";
import { urlPublik } from "@/lib/baseUrl";

// Every price, amount and total shown here comes from the server (preview / save);
// the page only collects what the customer orders.
const rupiah = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const TIPE_LABEL = { ENDUSER: "Enduser", RESELLER: "Reseller", MEMBER: "Member" };
const inputCls = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const labelCls = "text-[13px] font-semibold text-slate-200";
const newLine = () => ({ barang_id: "", quantity: 1, panjang_cm: "", lebar_cm: "", panjang_m: "", disc: 0, disc_alasan: "", harga_manual: "",
  hk_on: false, harga_khusus: "", harga_khusus_alasan: "", nama_file: "", link_file: "", finishing: "" });

const JUDUL = { DIGITAL_PRINT: "Transaksi Digital Print", DTF: "Transaksi DTF (sablon kaos)" };

// F1.5-G: the same form serves Digital Print and DTF; each lists its own barang.
export default function TransaksiDigitalPrint({ jenis = "DIGITAL_PRINT" }) {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";  // HARGA-KHUSUS: the floor hint differs (the server decides)
  const kirim = useKunciKirim(); // paket 1: a retry of the same order is saved once
  const isDtf = jenis === "DTF";
  const cetak = useCetak();
  const [customers, setCustomers] = React.useState([]);
  const [designers, setDesigners] = React.useState([]);
  const [bawaan, setBawaan] = React.useState(null);  // v2.21: default designer per door
  const [barang, setBarang] = React.useState([]);
  const [cfg, setCfg] = React.useState(null);
  const [custQuery, setCustQuery] = React.useState("");
  const [header, setHeader] = React.useState({ customer_id: "", designer_id: "", jenis_file: "", catatan: "", diskon: 0, layanan: "NORMAL" });
  const [lines, setLines] = React.useState([newLine()]);
  const [preview, setPreview] = React.useState(null);
  const [saved, setSaved] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    Promise.all([apiListCustomers(), apiListDesigners(), apiListBarang({ aktif: true }), apiGetPengaturan(),
      isDtf ? apiDesainerBawaan().catch(() => null) : null])
      .then(([c, d, b, p, bw]) => {
        const lain = [p.dtf_kategori_barang, p.jersey_kategori_barang, "CUSTOM"];
        setCustomers(c); setDesigners(d); setCfg(p); setBawaan(bw);
        setBarang(b.filter((x) => (isDtf ? x.kategori_kode === p.dtf_kategori_barang : !lain.includes(x.kategori_kode))));
        // v2.21: DTF starts with the Owner's default designer ("" = every designer)
        setHeader((h) => ({ ...h, jenis_file: p.jenis_file[0] || "", ...(isDtf ? { designer_id: desainerAwal(bw, "DTF", d) } : {}) }));
      })
      .catch((e) => setError(errText(e)));
  }, [isDtf]);

  const customer = customers.find((c) => c.id === header.customer_id);
  const q = custQuery.trim().toLowerCase();
  const custMatches = q ? customers.filter((c) => `${c.kode_customer || ""} ${c.nama} ${c.nomor_telepon}`.toLowerCase().includes(q)).slice(0, 8) : [];
  const barangById = Object.fromEntries(barang.map((b) => [b.id, b]));

  const change = (fn) => { fn(); setPreview(null); };
  const setLine = (i, patch) => change(() => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l))));

  const payload = () => ({
    ...header,
    designer_id: header.designer_id || null,  // v2.21: DTF may leave it to every designer (Digital Print: required)
    diskon: parseInt(header.diskon, 10) || 0,
    diskon_alasan: (parseInt(header.diskon, 10) || 0) > 0 ? (header.diskon_alasan || "").trim() || null : null,  // HARGA-KHUSUS
    tanpa_ppn_alasan: header.tanpa_ppn_alasan || null,
    items: lines.map((l) => ({
      barang_id: l.barang_id,
      quantity: parseInt(l.quantity, 10) || 0,
      // F1.5-B: whole centimetres; the server computes m² and the price
      // hotfix 2026-10-09: a per-metre (meter lari) barang takes the REAL length in metres -> cm
      ...(barangById[l.barang_id]?.mode_harga === "PER_METER_LARI"
        ? { panjang_cm: meterKeCm(l.panjang_m), lebar_cm: null }
        : { panjang_cm: l.panjang_cm === "" ? null : parseInt(l.panjang_cm, 10),
            lebar_cm: l.lebar_cm === "" ? null : parseInt(l.lebar_cm, 10) }),
      disc: parseInt(l.disc, 10) || 0,
      // F1.5-A: only sent for a "harga mulai dari" barang; the server checks the minimum
      ...(barangById[l.barang_id]?.harga_mulai_dari && l.harga_manual !== "" ? { harga_manual: parseInt(l.harga_manual, 10) || 0 } : {}),
      ...fieldHargaKhusus(l),  // HARGA-KHUSUS: special price + reasons (the server checks the floor)
      nama_file: l.nama_file || null,
      link_file: l.link_file || null,
      finishing: l.finishing || null,
    })),
  });

  const hitung = async () => {
    setBusy("hitung"); setError(null);
    try { setPreview(await apiPreviewTransaksi(jenis, payload())); } catch (e) { setPreview(null); setError(errText(e)); }
    finally { setBusy(null); }
  };

  const simpan = async () => {
    setBusy("simpan"); setError(null);
    try {
      setSaved(await apiCreateTransaksi(jenis, payload(), kirim.ambil()));
      kirim.selesai();
      setLines([newLine()]); setPreview(null); setCustQuery("");
      setHeader((h) => ({ ...h, customer_id: "", catatan: "", diskon: 0, diskon_alasan: "", tanpa_ppn_alasan: "" }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { setError(errText(e)); } finally { setBusy(null); }
  };

  const trackingUrl = saved ? urlPublik(`/tracking/${saved.order.tracking_token}`, process.env.REACT_APP_PUBLIC_URL, window.location.origin) : null;

  return (
    <div data-testid="transaksi-dp-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">{JUDUL[jenis]}</h1>
          <p className="text-sm text-slate-400 mt-1">Harga dihitung server dari master barang. Pembayaran dicatat di tahap berikutnya — transaksi tersimpan sebagai <b>Belum Bayar</b>.</p>
        </div>

        {isDtf && <DtfMenungguAcc key={saved?.order?.id || "awal"} />}
        {error && <div data-testid="trx-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}

        {saved && (
          <div data-testid="trx-saved" className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-5 flex flex-wrap gap-6 items-start">
            <div className="space-y-2 flex-1 min-w-[16rem]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-wider text-emerald-300">Transaksi tersimpan</span>
                <button data-testid="trx-saved-close" onClick={() => setSaved(null)} className="text-slate-400 hover:text-slate-200"><X size={16} /></button>
              </div>
              <div data-testid="trx-faktur" className="text-2xl font-mono text-amber-300">{saved.order.nomor_faktur}</div>
              <div className="text-sm text-slate-300">Total <b data-testid="trx-total">{rupiah(saved.order.total)}</b> · Belum Bayar
                {cetak.nota && <a data-testid="trx-cetak-nota" href={`/nota/${saved.nota.id}/cetak`} target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-xs text-cyan-300 hover:underline"><Printer size={12} /> Cetak nota</a>}</div>
              <div className="flex flex-wrap gap-3">
                <LinkPublik kind="nota" id={saved.nota.id} label="Salin link nota" testid="trx-link-nota" />
                <KirimLink notaId={saved.nota.id} testid="trx-kirim-link" />
                <LinkPublik kind="customer" id={saved.order.customer_id} label="Salin link cek order customer" testid="trx-link-customer" />
              </div>
              <div className="text-sm text-cyan-300">Rencana selesai {rencanaTeks(saved.order)} ({saved.order.layanan === "EXPRESS" ? "Express" : "Normal"})</div>
              <WaStatus orderId={saved.order.id} testid="trx-wa-status" />
              {saved.order.acc_customer && saved.order.acc_customer.status !== "DISETUJUI" && (
                <div data-testid="trx-menunggu-acc" className="space-y-1"><div className="text-xs text-amber-300">Menunggu ACC customer — produksi dimulai setelah ACC.</div>
                  <AccPanel orderId={saved.order.id} jenis={saved.order.jenis_transaksi} acc={saved.order.acc_customer} testid="trx-acc" /></div>
              )}
              {saved.stok?.peringatan?.map((w) => <div key={w} data-testid="trx-saved-stok" className="text-xs text-amber-300">⚠ {w}</div>)}
              {saved.order.menunggu_dp ? (
                <div data-testid="trx-menunggu-dp" className="text-xs text-amber-300">SPK menunggu DP — produksi dimulai setelah pembayaran minimal {rupiah(saved.nota.minimal_bayar_produksi)} tercatat.</div>
              ) : (
                <div data-testid="trx-belum-dp" className="text-xs text-slate-400">Belum ada DP. Pembayaran bisa dicatat sekarang atau nanti di menu Pembayaran.</div>
              )}
              <div className="space-y-1 pt-2">
                {saved.spks.map((s) => (
                  <div key={s.id} className="flex items-center gap-3 text-sm">
                    <span className="font-mono text-slate-300 w-40">{s.nomor_spk}</span>
                    <span className="text-slate-400 w-32">Mesin {s.jenis_mesin_nama}</span>
                    <span data-testid={`trx-spk-status-${s.grup_spk}`} className={`text-xs ${s.status_spk === "READY" ? "text-emerald-300" : "text-amber-300"}`}>
                      {s.status_spk === "READY" ? "masuk antrean produksi" : spkTunggu(s)}
                      {s.stok_kurang?.length > 0 && ` (${s.stok_kurang.map((k) => k.antre ? `${k.kode}: antre pesanan lebih lama` : `${k.kode} kurang`).join(", ")})`}</span>
                    {cetak.spk && <a data-testid={`trx-print-${s.grup_spk}`} href={`/spk/${s.id}/cetak`} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300"><Printer size={13} /> Cetak SPK</a>}
                  </div>
                ))}
              </div>
            </div>
            <div className="w-full order-last border-t border-emerald-500/20 pt-3">
              <div className="text-[13px] font-semibold text-slate-200 mb-2">Bayar sekarang (opsional)</div>
              <BayarForm notaId={saved.nota.id} ringkasan={{ total: saved.order.total, dibayar: 0, sisa: saved.order.total }} rekening={cfg?.rekening} testid="trx-bayar" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="bg-white p-2 rounded-lg"><QRCodeCanvas data-testid="trx-qr" value={trackingUrl} size={112} /></div>
              <a data-testid="trx-tracking-link" href={trackingUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-300 hover:underline">Link tracking customer</a>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2 relative">
            <label className={labelCls}>Customer</label>
            {customer ? (
              <div data-testid="trx-customer-selected" className="mt-1 flex items-center justify-between rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm">
                <span><span className="font-mono text-amber-300">{customer.kode_customer || "—"}</span> {customer.nama} <span className="text-slate-500">{customer.nomor_telepon}</span></span>
                <button data-testid="trx-customer-clear" onClick={() => change(() => setHeader({ ...header, customer_id: "" }))} className="text-slate-400 hover:text-slate-200"><X size={14} /></button>
              </div>
            ) : (
              <>
                <input data-testid="trx-customer-search" value={custQuery} onChange={(e) => setCustQuery(e.target.value)} placeholder="Ketik kode / nama / telepon" className={inputCls} />
                {custMatches.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 shadow-lg">
                    {custMatches.map((c) => (
                      <button key={c.id} data-testid={`trx-customer-option-${c.kode_customer}`} onClick={() => { change(() => setHeader({ ...header, customer_id: c.id })); setCustQuery(""); }}
                        className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-800">
                        <span className="font-mono text-amber-300">{c.kode_customer || "—"}</span> {c.nama} <span className="text-slate-500">{c.nomor_telepon} · {TIPE_LABEL[c.tipe_customer]}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label className={labelCls}>Type (tipe customer)</label>
            <div data-testid="trx-tipe" className="mt-1 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 text-sm text-cyan-300">{customer ? TIPE_LABEL[customer.tipe_customer] : "—"}</div>
          </div>
          <div>
            <label className={labelCls}>Design grafis</label>
            <select data-testid="trx-designer" value={header.designer_id} onChange={(e) => change(() => setHeader({ ...header, designer_id: e.target.value }))} className={inputCls}>
              <option value="">{isDtf ? "Semua desainer (siapa saja boleh Mulai)" : "— pilih —"}</option>
              {designers.map((d) => <option key={d.id} value={d.id}>{d.nama}</option>)}
            </select>
            {isDtf && <p className="mt-0.5 text-[11px] text-slate-500">{header.designer_id ? "Hanya desainer ini yang bisa menekan Mulai di Desain." : "Desainer mana pun bisa mengambil pekerjaan Desain."}</p>}
          </div>
          <div>
            <label className={labelCls}>Jenis file</label>
            <select data-testid="trx-jenis-file" value={header.jenis_file} onChange={(e) => change(() => setHeader({ ...header, jenis_file: e.target.value }))} className={inputCls}>
              {(cfg?.jenis_file || []).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className={labelCls}>Catatan</label>
            <input data-testid="trx-catatan" value={header.catatan} onChange={(e) => setHeader({ ...header, catatan: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Layanan</label>
            <select data-testid="trx-layanan" value={header.layanan} onChange={(e) => change(() => setHeader({ ...header, layanan: e.target.value }))} className={inputCls}>
              <option value="NORMAL">Normal</option><option value="EXPRESS">Express</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Diskon nota (Rp)</label>
            <input data-testid="trx-diskon" type="number" min="0" value={header.diskon} onChange={(e) => change(() => setHeader({ ...header, diskon: e.target.value }))} className={inputCls} />
          </div>
          <AlasanDiskon nilai={header.diskon} value={header.diskon_alasan} label="Alasan diskon nota" testid="trx-diskon-alasan"
            onChange={(v) => change(() => setHeader({ ...header, diskon_alasan: v }))} />
          <TanpaPpn testid="trx-tanpa-ppn" value={header.tanpa_ppn_alasan} onChange={(v) => change(() => setHeader({ ...header, tanpa_ppn_alasan: v }))} />
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-[15px] font-bold text-soq-biru-tua">Item</h2>
            <button data-testid="trx-add-line" onClick={() => change(() => setLines([...lines, newLine()]))} className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-amber-500/40 hover:text-amber-300"><Plus size={13} /> Tambah item</button>
          </div>
          {lines.map((l, i) => {
            const b = barangById[l.barang_id];
            const perM2 = b?.mode_harga === "PER_M2";
            const perMeter = b?.mode_harga === "PER_METER_LARI";
            const pv = preview?.items?.[i];
            return (
              <div key={i} data-testid={`trx-line-${i}`} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3 grid grid-cols-2 md:grid-cols-6 lg:grid-cols-12 gap-2 items-end">
                <div className="col-span-2 md:col-span-3 lg:col-span-3"><label className={labelCls}>Barang</label>
                  <select data-testid={`trx-barang-${i}`} value={l.barang_id} onChange={(e) => setLine(i, { barang_id: e.target.value })} className={inputCls}>
                    <option value="">— pilih —</option>
                    {barang.map((x) => <option key={x.id} value={x.id}>{x.kode} · {x.nama}</option>)}
                  </select></div>
                <div><label className={labelCls} title={perMeter ? "Total panjang = panjang × jumlah lembar, lalu dibulatkan SEKALI (minimal & kelipatan barang)" : undefined}>{perMeter ? "Jumlah lembar" : "Qty"}</label><input data-testid={`trx-qty-${i}`} type="number" min="1" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} className={inputCls} /></div>
                {perMeter ? (
                  <div className="col-span-2"><label className={labelCls}>Panjang sebenarnya (m)*{b.lebar_roll_cm ? ` · roll ${b.lebar_roll_cm} cm` : ""}</label>
                    <input data-testid={`trx-pm-${i}`} inputMode="decimal" value={l.panjang_m} placeholder="mis. 1,3" onChange={(e) => setLine(i, { panjang_m: e.target.value })} className={inputCls} /></div>
                ) : (<>
                <div><label className={labelCls}>P (cm){perM2 && "*"}</label><input data-testid={`trx-p-${i}`} type="number" min="1" step="1" value={l.panjang_cm} disabled={b && !perM2} onChange={(e) => setLine(i, { panjang_cm: e.target.value })} className={`${inputCls} disabled:opacity-40`} /></div>
                <div><label className={labelCls}>L (cm){perM2 && "*"}</label><input data-testid={`trx-l-${i}`} type="number" min="1" step="1" value={l.lebar_cm} disabled={b && !perM2} onChange={(e) => setLine(i, { lebar_cm: e.target.value })} className={`${inputCls} disabled:opacity-40`} /></div>
                </>)}
                {b?.harga_mulai_dari && (
                  <div><label className={labelCls} title="Harga mulai dari: boleh dinaikkan, tidak boleh di bawah harga master">Harga (Rp)</label>
                    <input data-testid={`trx-harga-${i}`} type="number" min="0" value={l.harga_manual} placeholder="harga master" onChange={(e) => setLine(i, { harga_manual: e.target.value })} className={inputCls} /></div>
                )}
                <div><label className={labelCls}>Disc (Rp)</label><input data-testid={`trx-disc-${i}`} type="number" min="0" value={l.disc} onChange={(e) => setLine(i, { disc: e.target.value })} className={inputCls} /></div>
                <AlasanDiskon nilai={l.disc} value={l.disc_alasan} label="Alasan disc" testid={`trx-disc-alasan-${i}`} onChange={(v) => setLine(i, { disc_alasan: v })} />
                <div className="col-span-2 lg:col-span-2"><label className={labelCls}>Nama file</label><input data-testid={`trx-file-${i}`} value={l.nama_file} onChange={(e) => setLine(i, { nama_file: e.target.value })} className={inputCls} /></div>
                <div className="col-span-2 lg:col-span-2"><label className={labelCls}>Finishing</label><input data-testid={`trx-finishing-${i}`} value={l.finishing} onChange={(e) => setLine(i, { finishing: e.target.value })} placeholder="mis. mata ayam" className={inputCls} /></div>
                <button data-testid={`trx-remove-${i}`} disabled={lines.length === 1} onClick={() => change(() => setLines(lines.filter((_, idx) => idx !== i)))} className="rounded-md border border-slate-700 bg-slate-800/60 p-2 text-slate-400 hover:text-red-300 disabled:opacity-30 justify-self-end"><Trash2 size={14} /></button>
                {b && <div className={l.hk_on ? "col-span-full" : "col-span-2 md:col-span-2 lg:col-span-2 self-center"}>
                  <HargaKhususField line={l} barang={b} owner={isOwner} testid={`trx-hk-${i}`} onChange={(patch) => setLine(i, patch)} /></div>}
                <div className="col-span-2 md:col-span-4 lg:col-span-6"><label className={labelCls}>Link file (opsional)</label><input data-testid={`trx-link-${i}`} value={l.link_file} onChange={(e) => setLine(i, { link_file: e.target.value })} placeholder="https://drive.google.com/…" className={inputCls} /></div>
                <div className="col-span-2 md:col-span-2 lg:col-span-6 text-right text-sm" data-testid={`trx-line-result-${i}`}>
                  {b && perM2 && luasM2(l.panjang_cm, l.lebar_cm, l.quantity) && <span data-testid={`trx-luas-${i}`} className="text-xs text-slate-400 mr-3">Luas {luasM2(l.panjang_cm, l.lebar_cm, l.quantity)} m² · harga per m²</span>}
                  {perMeter && pv?.panjang_ditagih_cm == null && <span data-testid={`trx-meter-info-${i}`} className="text-xs text-slate-500 mr-3">Total panjang = panjang × jumlah lembar, dibulatkan sekali</span>}
                  {perMeter && pv?.panjang_ditagih_cm != null && <span data-testid={`trx-meter-${i}`} className="text-xs text-slate-400 mr-3">Sebenarnya {teksMeter(pv.panjang_nyata_cm)} m{parseInt(l.quantity, 10) > 1 ? ` (${l.quantity} lembar)` : ""} · ditagih {teksMeter(pv.panjang_ditagih_cm)} m lari</span>}
                  {pv?.sumber_harga === "KHUSUS" && <span data-testid={`trx-hk-info-${i}`} className="text-xs text-amber-700 mr-3">Harga khusus {rupiah(pv.harga)} (normal {rupiah(pv.harga_normal)})</span>}
                  {pv && <span className="font-mono text-slate-200"><b>{rupiah(pv.jumlah)}</b></span>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 flex flex-wrap items-center gap-4">
          {preview ? (
            <div data-testid="trx-preview" className="flex-1 space-y-1">
              <div className="text-[13px] font-semibold text-slate-200">Rincian hitungan</div>
              <RincianHitung rincian={preview.rincian} testid="trx-rincian" />
              <div data-testid="trx-rencana" className="text-sm text-cyan-300">Rencana selesai: <b>{rencanaTeks(preview)}</b> ({preview.layanan === "EXPRESS" ? "Express" : "Normal"}, {waktuProduksiTeks(preview.waktu_produksi, preview.hari_produksi)})</div>
              <div className="text-xs text-slate-500">Akan dibuat {preview.spk.length} SPK: {preview.spk.map((g) => `Mesin ${g.jenis_mesin_nama} (${g.items.length} item)`).join(" · ")}</div>
              {preview.stok?.pemakaian?.length > 0 && (
                <div data-testid="trx-pemakaian" className="text-xs text-slate-500">Bahan terpakai: {preview.stok.pemakaian.flatMap((g) => g.bahan).map((b) => `${b.kode} ${qty(b.qty_milli)} ${b.satuan || ""}`).join(" · ")}</div>
              )}
              {preview.stok?.peringatan?.map((w) => <div key={w} data-testid="trx-stok-peringatan" className="text-xs text-amber-300">⚠ {w}</div>)}
            </div>
          ) : (
            <div className="flex-1 text-sm text-slate-500">Tekan <b>Hitung</b> untuk melihat harga dari server sebelum menyimpan.</div>
          )}
          <button data-testid="trx-hitung" onClick={hitung} disabled={!!busy} className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:border-amber-500/50 disabled:opacity-50">
            {busy === "hitung" ? <Loader2 className="animate-spin" size={16} /> : <Calculator size={16} />} Hitung
          </button>
          <button data-testid="trx-simpan" onClick={simpan} disabled={!!busy || !preview} title={preview ? "" : "Hitung dulu"} className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50">
            {busy === "simpan" ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Simpan (Belum Bayar)
          </button>
        </div>
      </main>
    </div>
  );
}
